import type {
  AgentBuilderCatalogView,
  BuilderFieldOption,
  DashboardAnalysisActiveFilter,
  DashboardAnalysisContext,
  DashboardAnalysisFieldReference,
  DashboardCatalogPlatform,
  DashboardDefinition,
  DashboardFilter,
  DashboardRuntimeDateRange,
  DashboardRuntimeState,
  DashboardRuntimeWidgetState,
  DashboardSlicerPanel,
  DashboardWidget,
  DaxGenerationRequest,
  DaxGenerationRequestFilter,
  DatePresetOption,
} from './types'
import { getGaugeMeasures } from './gauge'

/** Filter Type the API resolves against the Power BI 'Date'[_…] preset flag columns. */
export const DATE_PRESET_FILTER_TYPE = 'datePreset'
export const DATE_PRESET_CURRENT_FILTER = '=='
export const DATE_PRESET_PREVIOUS_YEAR_FILTER = 'previousYear'

export function isDatePresetFilter(filter: Pick<DaxGenerationRequestFilter, 'Type'> | null | undefined) {
  return String(filter?.Type ?? '').trim().toLowerCase() === DATE_PRESET_FILTER_TYPE.toLowerCase()
}

/** Previous-year comparison: the API shifts the preset window back 12 months. */
export function toPreviousYearDatePresetFilters(filters: DaxGenerationRequestFilter[]): DaxGenerationRequestFilter[] {
  return filters.map((filter) => (
    isDatePresetFilter(filter) ? { ...filter, Filter: DATE_PRESET_PREVIOUS_YEAR_FILTER } : filter
  ))
}

export function findDatePresetOption(
  options: DatePresetOption[] | null | undefined,
  value: string | null | undefined,
): DatePresetOption | null {
  if (!value) return null
  return (options ?? []).find((option) => option.value === value) ?? null
}

export function getDefaultDatePresetOption(options: DatePresetOption[] | null | undefined): DatePresetOption | null {
  return (options ?? []).find((option) => option.isDefault) ?? null
}

function normalizeFieldName(value: string) {
  return String(value ?? '').trim().toLowerCase()
}

function normalizeSlicerSelection(value: string | string[] | null | undefined): string | string[] | null {
  if (Array.isArray(value)) {
    const normalized = value
      .map((item) => String(item ?? '').trim())
      .filter((item) => item && item.toLowerCase() !== 'all')
    return normalized.length > 0 ? normalized : null
  }

  const normalized = String(value ?? '').trim()
  if (!normalized) return null
  if (normalized.toLowerCase() === 'all') return null
  return normalized
}

function dedupeFilters(filters: DaxGenerationRequestFilter[]) {
  const seen = new Set<string>()
  return filters.filter((filter) => {
    const key = `${normalizeFieldName(filter.Field)}|${String(filter.Filter)}|${String(filter.Value)}`
    if (seen.has(key)) return false
    seen.add(key)
    return true
  })
}

function dedupeMeasures(measures: string[]) {
  const seen = new Set<string>()
  return measures.filter((measure) => {
    const key = normalizeFieldName(measure)
    if (!key || seen.has(key)) return false
    seen.add(key)
    return true
  })
}

function isPreviousYearComparisonMeasure(measure: string) {
  const normalized = normalizeFieldName(measure)
    .replace(/['"[\]()]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()

  return /\b(ly|last year|previous year|prior year|py)\b/.test(normalized)
}

export function defaultMeasureParameterSelections(widget: DashboardWidget): Record<string, string> {
  const initial: Record<string, string> = {}
  for (const parameter of widget.measureParameters ?? []) {
    const defaultId = parameter.defaultOptionId ?? parameter.options[0]?.id
    if (defaultId) initial[parameter.id] = defaultId
  }
  return initial
}

function resolveSelectedParameterMeasures(
  widget: DashboardWidget,
  widgetRuntime: DashboardRuntimeWidgetState,
) {
  const selections = widgetRuntime.measureParameterSelections ?? {}
  const ownerByMeasureKey = new Map<string, string>()
  const selectedByParameterId = new Map<string, string>()

  for (const parameter of widget.measureParameters ?? []) {
    for (const option of parameter.options) {
      const key = normalizeFieldName(option.measure)
      if (key && !ownerByMeasureKey.has(key)) ownerByMeasureKey.set(key, parameter.id)
    }

    const selectedOptionId = selections[parameter.id] ?? parameter.defaultOptionId ?? parameter.options[0]?.id
    const selected = parameter.options.find((option) => option.id === selectedOptionId) ?? parameter.options[0]
    if (selected?.measure) selectedByParameterId.set(parameter.id, selected.measure)
  }

  return { ownerByMeasureKey, selectedByParameterId }
}

function applyMeasureParameterSelections(
  widget: DashboardWidget,
  { ownerByMeasureKey, selectedByParameterId }: ReturnType<typeof resolveSelectedParameterMeasures>,
  measures: string[],
): string[] {
  if ((widget.measureParameters ?? []).length === 0) return measures

  const placedParameterIds = new Set<string>()
  const result: string[] = []

  // Swap each parameter-owned measure for the selected option in place, so the
  // selection keeps the base measure's position in the request.
  for (const measure of measures) {
    const ownerId = ownerByMeasureKey.get(normalizeFieldName(measure))
    if (!ownerId) {
      result.push(measure)
      continue
    }
    if (placedParameterIds.has(ownerId)) continue
    placedParameterIds.add(ownerId)
    const selected = selectedByParameterId.get(ownerId)
    if (selected) result.push(selected)
  }

  for (const [parameterId, measure] of selectedByParameterId) {
    if (!placedParameterIds.has(parameterId)) result.push(measure)
  }

  return dedupeMeasures(result)
}

function resolveActiveMeasures(widget: DashboardWidget, measures: string[], preferredMeasures: string[] = []) {
  // A gauge asks for exactly the measures its contract names (actual, then target), in that order,
  // whatever else the JSON lists, so the response columns map to those roles by position.
  if (widget.type === 'gauge') return getGaugeMeasures(widget)

  const dedupedMeasures = dedupeMeasures(measures)
  if (widget.type !== 'line') {
    return dedupedMeasures
  }

  // Line widgets plot a single measure; an explicit measure-parameter choice wins.
  const preferredKeys = new Set(preferredMeasures.map(normalizeFieldName))
  const preferred = dedupedMeasures.find((measure) => preferredKeys.has(normalizeFieldName(measure)))
  if (preferred) return [preferred]

  const currentPeriodMeasures = dedupedMeasures.filter((measure) => !isPreviousYearComparisonMeasure(measure))
  return (currentPeriodMeasures.length > 0 ? currentPeriodMeasures : dedupedMeasures).slice(0, 1)
}

function dedupeStrings(values: string[]) {
  const seen = new Set<string>()
  return values.filter((value) => {
    const key = normalizeFieldName(value)
    if (!key || seen.has(key)) return false
    seen.add(key)
    return true
  })
}

function isAgentBuilderCatalogView(
  catalog: DashboardCatalogPlatform[] | AgentBuilderCatalogView | null | undefined,
): catalog is AgentBuilderCatalogView {
  return Boolean(catalog && !Array.isArray(catalog) && 'fieldDimensionOptions' in catalog)
}

function buildFieldLookup(fields: BuilderFieldOption[] | undefined) {
  const lookup = new Map<string, BuilderFieldOption>()
  for (const field of fields ?? []) {
    const valueKey = normalizeFieldName(field.value)
    const nameKey = normalizeFieldName(field.name)
    if (valueKey && !lookup.has(valueKey)) lookup.set(valueKey, field)
    if (nameKey && !lookup.has(nameKey)) lookup.set(nameKey, field)
  }
  return lookup
}

function resolveFieldReference(
  rawValue: string | null | undefined,
  lookup: Map<string, BuilderFieldOption>,
  fallbackName?: string | null,
): DashboardAnalysisFieldReference | null {
  const value = String(rawValue ?? '').trim()
  if (!value) return null

  const resolved = lookup.get(normalizeFieldName(value))
  if (resolved) {
    return { ...resolved, resolved: true }
  }

  return {
    name: fallbackName?.trim() || value,
    value,
    type: '',
    resolved: false,
  }
}

function dedupeFieldReferences(fields: Array<DashboardAnalysisFieldReference | null | undefined>) {
  const seen = new Set<string>()
  const result: DashboardAnalysisFieldReference[] = []
  for (const field of fields) {
    if (!field) continue
    const key = normalizeFieldName(field.value) || normalizeFieldName(field.name)
    if (!key || seen.has(key)) continue
    seen.add(key)
    result.push(field)
  }
  return result
}

export function getCatalogDatePresetOptions(
  dashboard: DashboardDefinition,
  catalog: DashboardCatalogPlatform[] | AgentBuilderCatalogView | null | undefined,
): DatePresetOption[] {
  if (isAgentBuilderCatalogView(catalog)) return catalog.datePresetOptions ?? []
  const platformCatalog = catalog?.find((entry) => entry.platformTypeId === dashboard.platformTypeId)
    ?? catalog?.[0]
  return platformCatalog?.datePresetOptions ?? []
}

function getCatalogFields(
  dashboard: DashboardDefinition,
  catalog: DashboardCatalogPlatform[] | AgentBuilderCatalogView | null | undefined,
) {
  if (isAgentBuilderCatalogView(catalog)) {
    return {
      dimensions: catalog.fieldDimensionOptions ?? [],
      measures: catalog.fieldMeasureOptions ?? [],
    }
  }

  const platformCatalog = Array.isArray(catalog)
    ? catalog.find((entry) => entry.platformTypeId === dashboard.platformTypeId)
    : null

  return {
    dimensions: platformCatalog?.defaultDimensions ?? [],
    measures: platformCatalog?.defaultMeasures ?? [],
  }
}

function mapLegacyDashboardFiltersToSlicerPanel(filters: DashboardFilter[] | undefined): DashboardSlicerPanel | null {
  if (!filters?.length) return null
  return {
    position: 'top',
    clearAll: true,
    slicers: filters.map((filter) => ({
      field: filter.value,
      label: filter.name,
      control: 'dropdown',
      default: null,
    })),
  }
}

export function getEffectiveSlicerPanel(dashboard: DashboardDefinition | null): DashboardSlicerPanel | null {
  if (!dashboard) return null
  return dashboard.slicerPanel ?? mapLegacyDashboardFiltersToSlicerPanel(dashboard.dashboardFilters)
}

function resolveActiveDimension(widget: DashboardWidget, widgetRuntime: DashboardRuntimeWidgetState, baseDimensions: string[]) {
  if (widgetRuntime.groupByDimensions?.length) {
    return widgetRuntime.groupByDimensions[0] ?? null
  }

  if (widgetRuntime.groupByDimension) {
    return widgetRuntime.groupByDimension
  }

  const drillDepth = widgetRuntime.drillPath?.length ?? 0
  if (drillDepth > 0) {
    const drilledDimension = widget.drillDimensions?.[drillDepth - 1]
    if (drilledDimension) {
      return drilledDimension
    }
  }

  return widget.rowDimension ?? baseDimensions[0] ?? null
}

export function createEmptyDashboardDateRange(): DashboardRuntimeDateRange {
  return { mode: 'none', preset: null, from: null, to: null }
}

export function createEmptyDashboardRuntimeState(): DashboardRuntimeState {
  return {
    slicerValues: {},
    dateRange: createEmptyDashboardDateRange(),
    widgetStateById: {},
  }
}

/**
 * Resolves the date control's initial state from slicerPanel.dateRange.
 *
 * - mode "none"   -> no date filter.
 * - mode "custom" -> the stored customRange.
 * - mode "preset" -> the stored preset when the client's catalog lists it. Once the
 *   catalog has settled, a preset it does not list (including an empty or failed
 *   catalog) falls back to the stored customRange, or an empty custom range, so the
 *   viewer degrades to the manual picker instead of querying a period the model
 *   does not publish. Before the catalog settles the stored preset is provisional.
 * - no mode (older dashboards; legacy custom / noDateFilter tokens are already
 *   coerced to a mode by coerceDashboardDefinition) -> the catalog's isDefault
 *   preset. When the catalog has not loaded, failed, or has no default (e.g. it
 *   publishes no presets), the custom range is used as above.
 *
 * datePresetOptions is undefined/null until the catalog settles; callers pass []
 * for a failed catalog.
 */
export function resolveInitialDashboardDateRange(
  dateRange: DashboardSlicerPanel['dateRange'] | null | undefined,
  datePresetOptions?: DatePresetOption[] | null,
): DashboardRuntimeDateRange {
  if (!dateRange) return createEmptyDashboardDateRange()

  const customRange = dateRange.customRange
  const toCustom = (): DashboardRuntimeDateRange => ({
    mode: 'custom',
    preset: null,
    from: customRange?.from || null,
    to: customRange?.to || null,
  })

  if (dateRange.mode === 'none') return createEmptyDashboardDateRange()
  if (dateRange.mode === 'custom') return toCustom()

  const storedPreset = dateRange.mode === 'preset' ? String(dateRange.preset ?? '').trim() : ''
  if (storedPreset) {
    if (!datePresetOptions || findDatePresetOption(datePresetOptions, storedPreset)) {
      return { mode: 'preset', preset: storedPreset, from: null, to: null }
    }
    return toCustom()
  }

  const defaultOption = getDefaultDatePresetOption(datePresetOptions)
  if (defaultOption) {
    return { mode: 'preset', preset: defaultOption.value, from: null, to: null }
  }

  return toCustom()
}

/**
 * True when resolveInitialDashboardDateRange's result depends on the catalog's
 * date presets: a stored preset (kept only if the catalog lists it) or no mode
 * (the catalog default). Explicit none/custom modes do not.
 */
export function dateRangeDependsOnCatalogPresets(
  dateRange: DashboardSlicerPanel['dateRange'] | null | undefined,
): boolean {
  if (!dateRange) return false
  return dateRange.mode !== 'none' && dateRange.mode !== 'custom'
}

export function areDateRangesEqual(left: DashboardRuntimeDateRange, right: DashboardRuntimeDateRange): boolean {
  return left.mode === right.mode
    && (left.preset ?? null) === (right.preset ?? null)
    && (left.from ?? null) === (right.from ?? null)
    && (left.to ?? null) === (right.to ?? null)
}

export function initializeDashboardRuntimeState(
  dashboard: DashboardDefinition | null,
  datePresetOptions?: DatePresetOption[] | null,
): DashboardRuntimeState {
  const next = createEmptyDashboardRuntimeState()
  const slicerPanel = getEffectiveSlicerPanel(dashboard)
  if (!slicerPanel) return next

  for (const slicer of slicerPanel.slicers ?? []) {
    next.slicerValues[slicer.field] = Array.isArray(slicer.default)
      ? slicer.default
      : typeof slicer.default === 'string'
        ? slicer.default
        : null
  }

  next.dateRange = resolveInitialDashboardDateRange(slicerPanel.dateRange, datePresetOptions)

  return next
}

export function getWidgetRuntimeState(runtimeState: DashboardRuntimeState, widgetId: string): DashboardRuntimeWidgetState {
  return runtimeState.widgetStateById[widgetId] ?? {}
}

/**
 * The page date control's filters: one datePreset filter for "preset", the
 * >= / <= pair for "custom", nothing for "none".
 */
export function buildDateRangeFilters(
  dateField: string | null | undefined,
  dateRange: DashboardRuntimeDateRange | null | undefined,
): DaxGenerationRequestFilter[] {
  if (!dateField || !dateRange) return []

  if (dateRange.mode === 'preset') {
    return dateRange.preset
      ? [{ Type: DATE_PRESET_FILTER_TYPE, Field: dateField, Filter: DATE_PRESET_CURRENT_FILTER, Value: dateRange.preset }]
      : []
  }

  if (dateRange.mode !== 'custom') return []

  const filters: DaxGenerationRequestFilter[] = []
  if (dateRange.from) {
    filters.push({ Type: 'date', Field: dateField, Value: dateRange.from, Filter: '>=' })
  }
  if (dateRange.to) {
    filters.push({ Type: 'date', Field: dateField, Value: dateRange.to, Filter: '<=' })
  }
  return filters
}

export function buildEffectivePageFilters(
  dashboard: DashboardDefinition,
  runtimeState: DashboardRuntimeState,
): DaxGenerationRequestFilter[] {
  const filters: DaxGenerationRequestFilter[] = []
  const slicerPanel = getEffectiveSlicerPanel(dashboard)

  if (slicerPanel) {
    for (const slicer of slicerPanel.slicers ?? []) {
      const selected = normalizeSlicerSelection(runtimeState.slicerValues[slicer.field])
      if (selected) {
        filters.push({
          Type: 'string',
          Field: slicer.field,
          Value: selected,
          Filter: Array.isArray(selected) ? 'in' : '=',
        })
      }
    }

    filters.push(...buildDateRangeFilters(slicerPanel.dateRange?.field, runtimeState.dateRange))
  }

  return dedupeFilters(filters)
}

export function buildLegacySelectedFilters(
  dashboard: DashboardDefinition | null,
  runtimeState: DashboardRuntimeState,
): Record<string, string | string[]> {
  const selected: Record<string, string | string[]> = {}
  if (!dashboard?.dashboardFilters?.length) {
    return selected
  }

  dashboard.dashboardFilters.forEach((filter) => {
    const value = normalizeSlicerSelection(
      runtimeState.slicerValues[filter.value] ?? runtimeState.slicerValues[filter.name],
    )
    if (value) {
      selected[filter.value] = value
      selected[filter.name] = value
    }
  })

  return selected
}

export function buildEffectiveDaxGenerationRequest(
  dashboard: DashboardDefinition,
  widget: DashboardWidget,
  runtimeState: DashboardRuntimeState,
): DaxGenerationRequest {
  const base = widget.daxGenerationRequest ?? { dimensions: [], measures: [], filters: [] }
  const widgetRuntime = getWidgetRuntimeState(runtimeState, widget.id)

  const dimensions = [...(base.dimensions ?? [])]
  const parameterMeasures = resolveSelectedParameterMeasures(widget, widgetRuntime)
  const measures = applyMeasureParameterSelections(widget, parameterMeasures, [...(base.measures ?? [])])
  const pageFilters = buildEffectivePageFilters(dashboard, runtimeState)
  // At most one datePreset filter per request: the page date control wins over
  // any preset baked into the widget's own filters.
  const pageHasDatePreset = pageFilters.some(isDatePresetFilter)
  const filters: DaxGenerationRequestFilter[] = [
    ...(base.filters ?? []).filter((filter) => !(pageHasDatePreset && isDatePresetFilter(filter))),
    ...pageFilters,
  ]

  const requestedDimensions = dedupeStrings(
    widgetRuntime.groupByDimensions?.length
      ? widgetRuntime.groupByDimensions
      : [
          resolveActiveDimension(widget, widgetRuntime, dimensions),
        ].filter((value): value is string => Boolean(value)),
  )
  const activeDimensions = widget.type === 'line'
    ? requestedDimensions.slice(0, 1)
    : widget.type === 'gauge'
      ? []
      : requestedDimensions

  dimensions.splice(0, dimensions.length, ...activeDimensions)

  for (const drillFilter of widgetRuntime.drillPath ?? []) {
    filters.push({
      Type: 'string',
      Field: drillFilter.field,
      Value: drillFilter.value,
      Filter: '=',
    })
  }

  return {
    dimensions,
    measures: resolveActiveMeasures(
      widget,
      measures,
      [...parameterMeasures.selectedByParameterId.values()],
    ),
    filters: dedupeFilters(filters),
  }
}

export function buildDashboardAnalysisContext(
  dashboard: DashboardDefinition | null,
  runtimeState: DashboardRuntimeState,
  _slicerOptionsByField: Record<string, string[]> = {},
  catalog?: DashboardCatalogPlatform[] | AgentBuilderCatalogView | null,
): DashboardAnalysisContext | null {
  void _slicerOptionsByField
  if (!dashboard) return null

  const safeRuntimeState = runtimeState ?? createEmptyDashboardRuntimeState()
  const catalogFields = getCatalogFields(dashboard, catalog)
  const dimensionLookup = buildFieldLookup(catalogFields.dimensions)
  const measureLookup = buildFieldLookup(catalogFields.measures)
  const slicerPanel = getEffectiveSlicerPanel(dashboard)

  const dimensionRefs: Array<DashboardAnalysisFieldReference | null> = []
  const measureRefs: Array<DashboardAnalysisFieldReference | null> = []
  const activeFilters: DashboardAnalysisActiveFilter[] = []

  for (const widget of dashboard.widgets ?? []) {
    for (const dimension of widget.dimensions ?? []) {
      dimensionRefs.push(resolveFieldReference(dimension.value, dimensionLookup, dimension.name))
    }
    for (const dimension of widget.daxGenerationRequest?.dimensions ?? []) {
      dimensionRefs.push(resolveFieldReference(dimension, dimensionLookup))
    }
    if (widget.rowDimension) {
      dimensionRefs.push(resolveFieldReference(widget.rowDimension, dimensionLookup))
    }

    for (const measure of widget.measures ?? []) {
      measureRefs.push(resolveFieldReference(measure.value, measureLookup, measure.name))
    }
    for (const measure of widget.daxGenerationRequest?.measures ?? []) {
      measureRefs.push(resolveFieldReference(measure, measureLookup))
    }

    const widgetRuntime = getWidgetRuntimeState(safeRuntimeState, widget.id)
    for (const groupByDimension of widgetRuntime.groupByDimensions ?? []) {
      dimensionRefs.push(resolveFieldReference(groupByDimension, dimensionLookup))
    }
    if (widgetRuntime.groupByDimension) {
      dimensionRefs.push(resolveFieldReference(widgetRuntime.groupByDimension, dimensionLookup))
    }
  }

  for (const slicer of slicerPanel?.slicers ?? []) {
    const fieldRef = resolveFieldReference(slicer.field, dimensionLookup, slicer.label)
    dimensionRefs.push(fieldRef)
    const selected = normalizeSlicerSelection(safeRuntimeState.slicerValues[slicer.field])
    if (selected) {
      activeFilters.push({
        field: fieldRef?.value ?? slicer.field,
        type: fieldRef?.type || 'string',
        operator: Array.isArray(selected) ? 'in' : '=',
        value: selected,
        source: 'slicer',
        label: slicer.label,
        resolved: fieldRef?.resolved ?? false,
      })
    }
  }

  const dateField = slicerPanel?.dateRange?.field ?? null
  const dateFieldRef = resolveFieldReference(dateField, dimensionLookup)
  dimensionRefs.push(dateFieldRef)
  const presetOption = safeRuntimeState.dateRange.mode === 'preset'
    ? findDatePresetOption(getCatalogDatePresetOptions(dashboard, catalog), safeRuntimeState.dateRange.preset)
    : null
  if (dateFieldRef) {
    for (const filter of buildDateRangeFilters(dateFieldRef.value, safeRuntimeState.dateRange)) {
      const isPreset = isDatePresetFilter(filter)
      activeFilters.push({
        field: dateFieldRef.value,
        type: isPreset ? DATE_PRESET_FILTER_TYPE : dateFieldRef.type || 'date',
        operator: filter.Filter,
        value: filter.Value,
        source: 'dateRange',
        ...(isPreset && presetOption ? { label: presetOption.label } : {}),
        resolved: dateFieldRef.resolved ?? false,
      })
    }
  }

  const widgetSummaries = (dashboard.widgets ?? []).map((widget) => {
    const widgetRuntime = getWidgetRuntimeState(safeRuntimeState, widget.id)
    const groupByDimensions = dedupeStrings([
      ...(widgetRuntime.groupByDimensions ?? []),
      widgetRuntime.groupByDimension ?? '',
    ])

    return {
      id: widget.id,
      type: widget.type,
      title: widget.title,
      description: widget.description,
      dimensions: widget.dimensions ?? [],
      measures: widget.measures ?? [],
      filters: widget.filters ?? [],
      rowDimension: widget.rowDimension ?? null,
      groupByDimensions,
    }
  })

  return {
    activeFilters,
    runtimeDateRange: {
      field: dateFieldRef?.value ?? dateField,
      mode: safeRuntimeState.dateRange.mode,
      preset: safeRuntimeState.dateRange.mode === 'preset' ? safeRuntimeState.dateRange.preset : null,
      // For a preset the window is the model's resolved range (display/context only; the filter is the preset).
      from: safeRuntimeState.dateRange.mode === 'preset'
        ? presetOption?.from ?? null
        : safeRuntimeState.dateRange.mode === 'custom' ? safeRuntimeState.dateRange.from ?? null : null,
      to: safeRuntimeState.dateRange.mode === 'preset'
        ? presetOption?.to ?? null
        : safeRuntimeState.dateRange.mode === 'custom' ? safeRuntimeState.dateRange.to ?? null : null,
    },
    widgetSummaries,
    dashboardUsedDimensions: dedupeFieldReferences(dimensionRefs),
    dashboardUsedMeasures: dedupeFieldReferences(measureRefs),
  }
}
